import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import Dashboardnavbar from '../../layout/Dashboardnavbar';
import { BASE_URL } from '../../config/axios';
import axios from 'axios';
import {
    ChevronRight, Folder, FolderOpen, FileText, Layers, TrendingUp,
    AlertCircle, CheckCircle, Search, Filter, Calendar, MapPin,
    BarChart3, RefreshCw, Eye, Construction, WrenchIcon, Building, IndianRupee,
    X
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useAnimation, useInView } from 'framer-motion';
import { AddProjectName } from './Projectname/AddProjectName';
import { formatCurrencyIndian } from '../CustomFunctions';
import { SECTORS } from '../../config/sectors';

/* ══════════════════════════════════════════════
   ANIMATION VARIANTS
══════════════════════════════════════════════ */
const fadeUp = {
    hidden:  { opacity: 0, y: 24 },
    visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.45, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] } }),
};

const fadeIn = {
    hidden:  { opacity: 0 },
    visible: (i = 0) => ({ opacity: 1, transition: { duration: 0.35, delay: i * 0.06 } }),
};

const scaleIn = {
    hidden:  { opacity: 0, scale: 0.88 },
    visible: (i = 0) => ({ opacity: 1, scale: 1, transition: { duration: 0.4, delay: i * 0.08, ease: [0.34, 1.56, 0.64, 1] } }),
};

/* ══════════════════════════════════════════════
   KPI CARD FILTER CONFIG
   Maps each card key → what it does to the list
   paymentKey → which payment total the card filters / sorts by
══════════════════════════════════════════════ */
const KPI_FILTER_MAP = {
    total:       { filterStatus: 'all',         sortBy: 'progress', label: 'All Projects' },
    completed:   { filterStatus: 'completed',   sortBy: 'progress', label: 'Completed Projects' },
    inProgress:  { filterStatus: 'in-progress', sortBy: 'progress', label: 'In-Progress Projects' },
    notStarted:  { filterStatus: 'not-started', sortBy: 'progress', label: 'Not Started Projects' },
    totalBudget: { filterStatus: 'all',         sortBy: 'cost',     label: 'Sorted by Budget ↓' },
    aboutToBill: { filterStatus: 'all',         sortBy: 'billed',   label: 'Sorted by Billed ↓' },

    // ── Payment summary cards ──
    pClaimed:    { filterStatus: 'all', sortBy: 'payment', paymentKey: 'claimed',          label: 'Projects with Claimed' },
    pReceived:   { filterStatus: 'all', sortBy: 'payment', paymentKey: 'received',         label: 'Projects with Received' },
    pDeduction:  { filterStatus: 'all', sortBy: 'payment', paymentKey: 'deduction',        label: 'Projects with Deduction' },
    pBalClaim:   { filterStatus: 'all', sortBy: 'payment', paymentKey: 'balanceToClaim',   label: 'Pending to Claim' },
    pBalRecv:    { filterStatus: 'all', sortBy: 'payment', paymentKey: 'balanceToReceive', label: 'Pending to Receive' },
};

/* ══════════════════════════════════════════════
   HOOK: animated counter (rolls up 0 → target)
══════════════════════════════════════════════ */
function useCountUp(target, duration = 1200, decimals = 0, start = false) {
    const [val, setVal] = useState(0);
    useEffect(() => {
        if (!start || target === 0) { setVal(target); return; }
        let startTime = null;
        const num = parseFloat(target) || 0;
        const step = (ts) => {
            if (!startTime) startTime = ts;
            const progress = Math.min((ts - startTime) / duration, 1);
            const ease = 1 - Math.pow(1 - progress, 3);
            const current = ease * num;
            setVal(decimals > 0 ? parseFloat(current.toFixed(decimals)) : Math.round(current));
            if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
    }, [target, start, duration, decimals]);
    return val;
}

/* ══════════════════════════════════════════════
   COMPONENT: KPI Card — clickable + active state
══════════════════════════════════════════════ */
const KpiCard = ({ label, rawValue, displayValue, sub, color, icon, delay, cardKey, activeCardKey, onCardClick }) => {
    const ref    = useRef(null);
    const inView = useInView(ref, { once: true, margin: '-40px' });
    const isNumber  = typeof rawValue === 'number';
    const counted   = useCountUp(isNumber ? rawValue : 0, 1000, 0, inView && isNumber);
    const isActive  = activeCardKey === cardKey;
    const isAnyActive = activeCardKey !== null && activeCardKey !== 'total';

    return (
        <motion.div
            ref={ref}
            variants={scaleIn} custom={delay}
            initial="hidden" animate={inView ? 'visible' : 'hidden'}
            onClick={() => onCardClick && onCardClick(cardKey)}
            style={{
                ...styles.kpiCard,
                borderTop: `3px solid ${color}`,
                cursor: onCardClick ? 'pointer' : 'default',
                outline: isActive ? `2px solid ${color}` : '2px solid transparent',
                outlineOffset: '2px',
                background: isActive ? `${color}10` : '#f8fafc',
                opacity: isAnyActive && !isActive ? 0.65 : 1,
                transition: 'opacity 0.2s, background 0.2s, outline 0.2s',
            }}
            whileHover={{ y: -4, boxShadow: `0 8px 30px ${color}22`, transition: { duration: 0.2 } }}
            whileTap={{ scale: 0.97 }}
        >
            {/* active indicator badge */}
            <AnimatePresence>
                {isActive && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.5 }}
                        style={{
                            position: 'absolute', top: 8, right: 8,
                            width: 8, height: 8, borderRadius: '50%',
                            background: color, boxShadow: `0 0 6px ${color}`,
                        }}
                    />
                )}
            </AnimatePresence>

            <motion.div style={{ position: 'absolute', top: 12, right: 12, color, opacity: 0 }}
                animate={inView ? { opacity: isActive ? 0.3 : 0.15, rotate: [0, 5, 0] } : { opacity: 0 }}
                transition={{ duration: 0.6, delay: delay * 0.07 + 0.3 }}>
                {icon}
            </motion.div>

            <div style={{ fontSize: '11px', fontWeight: 600, color: isActive ? color : '#94a3b8', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: '6px', transition: 'color 0.2s' }}>
                {label}
            </div>
            <motion.div
                style={{ fontSize: '24px', fontWeight: 700, color: isActive ? color : '#0f172a', lineHeight: 1, transition: 'color 0.2s' }}
                initial={{ opacity: 0, y: 8 }} animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: delay * 0.07 + 0.15 }}>
                {isNumber ? counted : displayValue}
            </motion.div>

            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '5px' }}>
                {isActive ? (
                    <span style={{ color, fontWeight: 600 }}>
                        ✓ {KPI_FILTER_MAP[cardKey]?.label}
                    </span>
                ) : sub}
            </div>
        </motion.div>
    );
};

/* ══════════════════════════════════════════════
   COMPONENT: Active Filter Banner
══════════════════════════════════════════════ */
const ActiveFilterBanner = ({ activeCardKey, cardLabel, color, count, onClear }) => (
    <AnimatePresence>
        {activeCardKey && activeCardKey !== 'total' && (
            <motion.div
                initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                animate={{ opacity: 1, height: 'auto', marginBottom: 0 }}
                exit={{ opacity: 0, height: 0 }}
                style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '8px 14px', borderRadius: 8,
                    background: `${color}12`, border: `1px solid ${color}40`,
                    fontSize: '13px',
                }}
            >
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block' }}/>
                <span style={{ color: '#334155', fontWeight: 500 }}>
                    Showing: <strong style={{ color }}>{cardLabel}</strong>
                    <span style={{ color: '#94a3b8', marginLeft: 6 }}>· {count} project{count !== 1 ? 's' : ''}</span>
                </span>
                <motion.button
                    onClick={onClear}
                    whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', alignItems: 'center', padding: 2 }}
                >
                    <X size={14}/> <span style={{ fontSize: '12px', marginLeft: 3 }}>Clear</span>
                </motion.button>
            </motion.div>
        )}
    </AnimatePresence>
);

/* ══════════════════════════════════════════════
   COMPONENT: Animated Progress Bar
══════════════════════════════════════════════ */
const AnimBar = ({ pct, color, height = 6, delay = 0, radius = 3 }) => {
    const ref    = useRef(null);
    const inView = useInView(ref, { once: true, margin: '-20px' });
    return (
        <div ref={ref} style={{ height, background: '#f1f5f9', borderRadius: radius, overflow: 'hidden' }}>
            <motion.div style={{ height: '100%', background: color, borderRadius: radius }}
                initial={{ width: 0, opacity: 0 }}
                animate={inView ? { width: `${Math.min(pct, 100)}%`, opacity: 1 } : { width: 0, opacity: 0 }}
                transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }} />
        </div>
    );
};

/* ══════════════════════════════════════════════
   COMPONENT: SVG Ring that draws itself
══════════════════════════════════════════════ */
const AnimRing = ({ pct, color, bg, size = 44, strokeWidth = 5, fontSize = '10px', delay = 0 }) => {
    const ref    = useRef(null);
    const inView = useInView(ref, { once: true, margin: '-20px' });
    const r      = (size / 2) - strokeWidth;
    const CIRC   = 2 * Math.PI * r;
    const target = (pct / 100) * CIRC;

    return (
        <div ref={ref} style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={bg} strokeWidth={strokeWidth} />
                <motion.circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    initial={{ strokeDasharray: `0 ${CIRC}`, opacity: 0 }}
                    animate={inView
                        ? { strokeDasharray: `${target} ${CIRC}`, opacity: 1 }
                        : { strokeDasharray: `0 ${CIRC}`, opacity: 0 }}
                    transition={{ duration: 1.2, delay, ease: [0.22, 1, 0.36, 1] }}
                    transform={`rotate(-90 ${size/2} ${size/2})`} />
            </svg>
            <motion.span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize, fontWeight: 700, color }}
                initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: 0.4, delay: delay + 0.5 }}>
                {pct}%
            </motion.span>
        </div>
    );
};

/* ══════════════════════════════════════════════
   COMPONENT: Skeleton shimmer placeholder
══════════════════════════════════════════════ */
const Skeleton = ({ w = '100%', h = 16, r = 6, style = {} }) => (
    <div style={{ width: w, height: h, borderRadius: r, background: 'linear-gradient(90deg, #f1f5f9 25%, #e8edf3 50%, #f1f5f9 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite', ...style }} />
);

const SkeletonKpiCard = () => (
    <div style={{ ...styles.kpiCard, borderTop: '3px solid #e2e8f0' }}>
        <Skeleton w="55%" h={11} r={4} style={{ marginBottom: 10 }} />
        <Skeleton w="45%" h={28} r={6} style={{ marginBottom: 8 }} />
        <Skeleton w="70%" h={11} r={4} />
    </div>
);

const SkeletonProjectCard = ({ index }) => (
    <motion.div variants={fadeUp} custom={index} initial="hidden" animate="visible"
        style={{ ...styles.projCard, padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <Skeleton w={40} h={40} r={10} />
            <div style={{ flex: 1 }}>
                <Skeleton w="40%" h={14} r={5} style={{ marginBottom: 8 }} />
                <Skeleton w="65%" h={11} r={4} />
            </div>
            <Skeleton w={44} h={44} r={22} />
        </div>
        <div style={{ marginTop: 14 }}>
            <Skeleton w="100%" h={6} r={3} />
        </div>
    </motion.div>
);

/* ══════════════════════════════════════════════
   HELPERS
══════════════════════════════════════════════ */
const getHealth = (pct) => {
    if (pct > 90) return 'green';
    if (pct >= 40)  return 'blue';
    if (pct > 0)    return 'amber';
    return 'red';
};

const HEALTH = {
    green: { folder: '#EAF3DE', icon: '#3B6D11', ring: '#3B6D11', bar: '#3B6D11' },
    blue:  { folder: '#E6F1FB', icon: '#185FA5', ring: '#185FA5', bar: '#185FA5' },
    amber: { folder: '#FAEEDA', icon: '#BA7517', ring: '#BA7517', bar: '#BA7517' },
    red:   { folder: '#FCEBEB', icon: '#A32D2D', ring: '#E24B4A', bar: '#E24B4A' },
};
const BADGE_LABEL = { green: 'Completed', blue: 'In Progress', amber: 'In Progress', red: 'Behind / Not Started' };
const chipColors  = {
    green: { background: '#EAF3DE', color: '#3B6D11' },
    blue:  { background: '#E6F1FB', color: '#185FA5' },
    amber: { background: '#FAEEDA', color: '#BA7517' },
    red:   { background: '#FCEBEB', color: '#A32D2D' },
};

// Color per card key for banner
const CARD_COLOR = {
    total:       '#185FA5',
    completed:   '#3B6D11',
    inProgress:  '#BA7517',
    notStarted:  '#A32D2D',
    totalBudget: '#0F6E56',
    aboutToBill: '#534AB7',
    // payment cards
    pClaimed:    '#185FA5',
    pReceived:   '#1D9E75',
    pDeduction:  '#E24B4A',
    pBalClaim:   '#EF9F27',
    pBalRecv:    '#7C3AED',
};

/* ══════════════════════════════════════════════
   PAYMENT HELPERS + BREAKDOWN PANEL
   Shows, for the active payment card, which section → workstage
   (→ submission) of a project holds the amount
══════════════════════════════════════════════ */
const PAYMENT_FIELD = {
    claimed:          'billingClaimedAmount',
    received:         'billingReceivedAmount',
    deduction:        'totalDeductionAmount',
    balanceToClaim:   'balanceToClaimAmount',
    balanceToReceive: 'balanceToReceiveAmount',
};
const readPayValue = (pd, key) => parseFloat(pd?.[PAYMENT_FIELD[key]] || 0) || 0;
const hasPayment = (pd) => !!pd && Object.keys(PAYMENT_FIELD).some((k) => readPayValue(pd, k) > 0);

// a "real" entry = something was actually claimed / received / deducted
const hasRealEntry = (pd) =>
    !!pd && (readPayValue(pd, 'claimed') > 0 || readPayValue(pd, 'received') > 0 || readPayValue(pd, 'deduction') > 0);

// Where is a workstage's payment saved?
// Input / Stage  → workstage.paymentData
// Submission     → workstage.paymentData (what the Payment modal saves today) wins;
//                  otherwise each submission_data[].paymentData (older records).
// Only ONE of them is used, so nothing is counted twice.
const getPaymentSources = (ws) => {
    if (ws.workstage_inputtype === 'Submission') {
        if (hasRealEntry(ws.paymentData)) return [{ label: 'Workstage payment', pd: ws.paymentData }];
        const subs = (ws.submission_data || [])
            .filter((s) => hasPayment(s.paymentData))
            .map((s) => ({ label: s.submissionName || 'Unnamed Submission', pd: s.paymentData }));
        if (subs.length) return subs;
        if (hasPayment(ws.paymentData)) return [{ label: 'Workstage payment', pd: ws.paymentData }];
        return [];
    }
    return hasPayment(ws.paymentData) ? [{ label: null, pd: ws.paymentData }] : [];
};
const fmtRs = (v) => `₹${formatCurrencyIndian(Number(Number(v).toFixed(2)))}`;

const PaymentBreakdown = ({ breakdown, label, color }) => {
    if (!breakdown || !breakdown.sections.length) return null;
    return (
        <div style={{ margin: '0 16px 12px', border: `1px solid ${color}30`, background: `${color}08`, borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color }}>{label} — by workstage</span>
                <span style={{ fontSize: '12px', fontWeight: 700, color }}>Total: {fmtRs(breakdown.total)}</span>
            </div>

            {breakdown.sections.map((sec) => (
                <div key={sec.id} style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: 600, color: '#334155', paddingBottom: 3, borderBottom: '1px dashed #e2e8f0' }}>
                        <span>{sec.name}</span>
                        <span>{fmtRs(sec.total)}</span>
                    </div>

                    {sec.workstages.map((ws) => (
                        <div key={ws.id} style={{ padding: '4px 0 2px 10px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: '12px' }}>
                                <span style={{ color: '#0f172a', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ws.name}</span>
                                    <span style={{ fontSize: '9px', padding: '1px 6px', borderRadius: 8, background: '#f1f5f9', color: '#64748b', fontWeight: 600, flexShrink: 0 }}>{ws.type}</span>
                                </span>
                                <span style={{ color, fontWeight: 700, flexShrink: 0 }}>{fmtRs(ws.total)}</span>
                            </div>

                            {/* submission workstages: one line per submission */}
                            {ws.type === 'Submission' && ws.rows.map((r, i) => (
                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: '11px', color: '#64748b', padding: '2px 0 0 14px' }}>
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>↳ {r.label}</span>
                                    <span style={{ fontWeight: 600, flexShrink: 0 }}>{fmtRs(r.value)}</span>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

/* ══════════════════════════════════════════════
   PER-PROJECT PAYMENT SUMMARY STRIP
   Shown inside every project card, under the Total / Billed / Remaining line
══════════════════════════════════════════════ */
const fmtCompact = (v) =>
    v >= 10000000
        ? `₹${formatCurrencyIndian(Number((v / 10000000).toFixed(2)))} Cr`
        : `₹${formatCurrencyIndian(Number((v / 100000).toFixed(2)))} Lacs`;

const PAYMENT_STRIP_ITEMS = [
    { key: 'claimed',          label: 'Claimed',        color: '#185FA5' },
    { key: 'received',         label: 'Received',       color: '#1D9E75' },
    { key: 'deduction',        label: 'Deduction',      color: '#E24B4A' },
    { key: 'balanceToClaim',   label: 'Bal. to Claim',  color: '#EF9F27' },
    { key: 'balanceToReceive', label: 'Bal. to Receive', color: '#7C3AED' },
];

const ProjectPaymentSummary = ({ pay, activeKey }) => {
    const hasAny = PAYMENT_STRIP_ITEMS.some((it) => (pay?.[it.key] || 0) > 0);

    // no payments entered → one slim line instead of a full block
    if (!hasAny) {
        return (
            <div style={{ margin: '0 16px 10px', borderTop: '1px solid #f1f5f9', paddingTop: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '10px', fontWeight: 600, color: '#94a3b8', letterSpacing: '.06em', textTransform: 'uppercase' }}>
                    Payment summary
                </span>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>No payment details entered yet</span>
            </div>
        );
    }

    return (
        <div style={{ margin: '0 16px 12px', borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
            <div style={{ fontSize: '10px', fontWeight: 600, color: '#94a3b8', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 6 }}>
                Payment summary
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 8 }}>
                    {PAYMENT_STRIP_ITEMS.map((it) => {
                        const isActive = activeKey === it.key;
                        return (
                            <div key={it.key}
                                style={{
                                    background: isActive ? `${it.color}14` : '#f8fafc',
                                    border: `1px solid ${isActive ? it.color + '60' : '#e2e8f0'}`,
                                    borderLeft: `3px solid ${it.color}`,
                                    borderRadius: 6, padding: '6px 10px',
                                }}>
                                <div style={{ fontSize: '10px', color: '#94a3b8', marginBottom: 2 }}>{it.label}</div>
                                <div style={{ fontSize: '13px', fontWeight: 700, color: it.color }}>{fmtCompact(pay?.[it.key] || 0)}</div>
                            </div>
                        );
                    })}
            </div>
        </div>
    );
};

const MetaItem = ({ icon, label }) => (
    <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '11px', color: '#94a3b8' }}>
        {icon} {label}
    </span>
);

const RailwaySvg = ({ active }) => (
    <svg width="20" height="20" viewBox="0 0 680 520">
        {[ ['rect',{x:333,y:40,width:14,height:50,rx:4,fill:'none',strokeWidth:15,strokeLinecap:'round'}],
           ['rect',{x:308,y:20,width:64,height:32,rx:8,fill:'none',strokeWidth:15}],
           ['rect',{x:170,y:90,width:340,height:260,rx:28,fill:'none',strokeWidth:15}],
           ['rect',{x:210,y:130,width:120,height:80,rx:8,fill:'none',strokeWidth:15}],
           ['rect',{x:350,y:130,width:120,height:80,rx:8,fill:'none',strokeWidth:15}],
           ['circle',{cx:250,cy:300,r:30,fill:'none',strokeWidth:15}],
           ['circle',{cx:250,cy:300,r:10}],
           ['circle',{cx:430,cy:300,r:30,fill:'none',strokeWidth:15}],
           ['circle',{cx:430,cy:300,r:10}],
        ].map(([Tag,props],i) => <Tag key={i} {...props} stroke={active?'white':'#707070'} fill={props.fill===undefined?(active?'white':'#707070'):props.fill} />)}
        {[['170,390','510,390'],['160,390','80,480'],['520,390','600,480'],['120,450','560,450'],['95,480','585,480']].map(([p1,p2],i)=>(
            <line key={i} x1={p1.split(',')[0]} y1={p1.split(',')[1]} x2={p2.split(',')[0]} y2={p2.split(',')[1]} stroke={active?'white':'#707070'} strokeWidth="15" strokeLinecap="round" opacity={i>2?i===3?.5:.3:1}/>
        ))}
    </svg>
);

const RopewaySvg = ({ active }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
        stroke={active ? 'white' : '#707070'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <line x1="2" y1="6" x2="22" y2="3" />
        <line x1="12" y1="4.5" x2="12" y2="8" />
        <rect x="7" y="8" width="10" height="9" rx="1.5" />
        <line x1="7" y1="12" x2="17" y2="12" />
        <line x1="12" y1="8" x2="12" y2="12" />
    </svg>
);

const HighwaySvg = ({ active }) => (
    <svg width="20" height="20" viewBox="0 0 680 580">
        {[['60,560','290,40',20],['230,560','310,40',20],['450,560','370,40',20],['620,560','390,40',20],['145,560','300,40',15],['340,560','340,40',15],['535,560','380,40',15]].map(([p1,p2,w],i)=>(
            <line key={i} x1={p1.split(',')[0]} y1={p1.split(',')[1]} x2={p2.split(',')[0]} y2={p2.split(',')[1]}
                stroke={active?'white':'#707070'} strokeWidth={w} strokeLinecap="round"
                strokeDasharray={i>3?'32 22':undefined}/>
        ))}
    </svg>
);

/* ══════════════════════════════════════════════
   STYLES
══════════════════════════════════════════════ */
const pillStyle = {
    display:'flex', alignItems:'center', gap:'5px',
    padding:'6px 12px', borderRadius:'20px', fontSize:'13px',
    fontWeight:500, cursor:'pointer', border:'1px solid', transition:'all .15s',
};
const styles = {
    page:          { padding:'20px 24px', maxWidth:1400, margin:'0 auto', display:'flex', flexDirection:'column', gap:'14px' },
    topBar:        { display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'10px', paddingBottom:'12px', borderBottom:'1px solid #f1f5f9' },
    pageTitle:     { fontSize:'20px', fontWeight:700, color:'#0f172a' },
    pageSub:       { fontSize:'12px', color:'#94a3b8', marginTop:'2px' },
    iconBtn:       { width:34, height:34, borderRadius:8, border:'1px solid #e2e8f0', background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'#64748b' },
    kpiRow:        { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:'10px' },
    kpiCard:       { position:'relative', background:'#f8fafc', borderRadius:10, padding:'14px 16px', overflow:'hidden', border:'1px solid #e2e8f0' },
    sectionLabel:  { fontSize:'11px', fontWeight:600, color:'#94a3b8', letterSpacing:'.06em', textTransform:'uppercase' },
    progressCard:  { background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, padding:'16px 20px' },
    analyticsRow:  { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))', gap:'12px' },
    analyticsCard: { background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, padding:'16px 20px' },
    analyticsTitle:{ fontSize:'13px', fontWeight:600, color:'#0f172a', marginBottom:'14px' },
    controls:      { display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' },
    searchWrap:    { flex:1, minWidth:'220px', display:'flex', alignItems:'center', gap:'8px', background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, padding:'7px 12px' },
    searchInput:   { border:'none', background:'transparent', fontSize:'13px', color:'#0f172a', outline:'none', flex:1, minWidth:0 },
    ctrlBtn:       { display:'flex', alignItems:'center', gap:'5px', padding:'7px 12px', borderRadius:8, border:'1px solid #e2e8f0', background:'#fff', fontSize:'13px', color:'#374151', cursor:'pointer', fontWeight:500, whiteSpace:'nowrap' },
    selectEl:      { padding:'7px 10px', borderRadius:8, border:'1px solid #e2e8f0', background:'#fff', fontSize:'13px', color:'#374151', cursor:'pointer', outline:'none' },
    projCard:      { background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, overflow:'hidden' },
    projHeader:    { display:'flex', alignItems:'center', gap:'14px', padding:'14px 16px', cursor:'pointer', transition:'background .15s' },
    codeChip:      { fontSize:'10px', padding:'2px 7px', borderRadius:4, background:'#f1f5f9', color:'#64748b', fontWeight:600, flexShrink:0 },
    statusChip:    { fontSize:'10px', padding:'2px 8px', borderRadius:10, fontWeight:600, flexShrink:0 },
    detailGrid:    { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:'8px', marginBottom:'16px' },
    detailItem:    { background:'#f8fafc', borderRadius:8, padding:'10px 12px' },
    eyeBtn:        { width:28, height:28, borderRadius:6, border:'1px solid #e2e8f0', background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'#64748b' },
};

const globalCSS = `
@keyframes shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
@keyframes pulse-dot {
  0%,100% { opacity:1; transform:scale(1); }
  50%      { opacity:.5; transform:scale(0.85); }
}
.ehd-live-dot {
  width:7px; height:7px; border-radius:50%; background:#22c55e;
  animation: pulse-dot 1.8s ease-in-out infinite;
  display:inline-block; margin-right:5px;
}
`;

/* ══════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════ */
const ProjectPageForHOD = () => {
    const navigate   = useNavigate();
    const urlParts   = window.location.href.split("/");
    const toggleshow = "Contractor";

    const [refreshTrigger, setRefreshTrigger]           = useState(0);
    const [toggleSector, setToggleSector]               = useState("null");
    const [projectList, setProjectList]                 = useState([]);
    const [sectionDetailsList, setSectionDetailsList]   = useState([]);
    const [openedProjects, setOpenedProjects]           = useState([]);
    const [openedSections, setOpenedSections]           = useState([]);
    const [openedWorkstages, setOpenedWorkstages]       = useState([]);
    const [loading, setLoading]                         = useState(true);
    const [dataLoaded, setDataLoaded]                   = useState(false);
    const [expandedAll, setExpandedAll]                 = useState(false);
    const [searchTerm, setSearchTerm]                   = useState('');
    const [filterStatus, setFilterStatus]               = useState('all');
    const [sortBy, setSortBy]                           = useState('progress');
    const [showFilters, setShowFilters]                 = useState(false);

    // active KPI card key (null = none active)
    const [activeCardKey, setActiveCardKey]             = useState(null);

    const ViewAllProjectDetails = (project) =>
        navigate(`/${urlParts[3]}/ProjectAllDetails/`, { state: { project, toggleshow } });

    /* ─── KPI card click handler ─── */
    const handleCardClick = useCallback((cardKey) => {
        // clicking the active card → deselect (reset)
        if (activeCardKey === cardKey || cardKey === 'total') {
            setActiveCardKey(cardKey === 'total' ? null : (activeCardKey === cardKey ? null : cardKey));
            setFilterStatus('all');
            setSortBy('progress');
            if (cardKey === 'total') setActiveCardKey(null);
            return;
        }
        const config = KPI_FILTER_MAP[cardKey];
        if (!config) return;
        setActiveCardKey(cardKey);
        setFilterStatus(config.filterStatus);
        setSortBy(config.sortBy);
        setOpenedProjects([]);
        setOpenedSections([]);
        setOpenedWorkstages([]);
        setExpandedAll(false);
    }, [activeCardKey]);

    const handleClearCardFilter = useCallback(() => {
        setActiveCardKey(null);
        setFilterStatus('all');
        setSortBy('progress');
    }, []);

    /* ─── APIs ─── */
    const getProjectData = async () => {
        try { return (await axios.get(`${BASE_URL}/dpr/projectdetailsmultiplefilter/null/${toggleSector}/null/null/null/null/null/null/`)).data; }
        catch { return []; }
    };
    const getSectionDetailsData = async () => {
        try { return (await axios.get(`${BASE_URL}/dpr/project/`)).data; }
        catch { return []; }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true); setDataLoaded(false);
            try {
                const [projects, sections] = await Promise.all([getProjectData(), getSectionDetailsData()]);
                setProjectList(projects || []); setSectionDetailsList(sections || []);
                setDataLoaded(true);
            } catch { setProjectList([]); setSectionDetailsList([]); }
            finally { setTimeout(() => setLoading(false), 500); }
        };
        fetchData();
    }, [refreshTrigger, toggleSector]);


    /* ─── Calculations ─── */
    const calculateWorkstageCompletedCost = useCallback((workstage) => {
        if (!workstage) return 0;
        let c = 0;
        if (workstage.workstage_inputtype === 'Input') {
            workstage.normal_input?.forEach(i => {
                if (i.input_type === 'Stage type')    i.stage?.forEach(s => { c += parseFloat(s.completed_work || 0); });
                else if (i.input_type === 'Normal Input') c += parseFloat(i.about_to_bill || 0);
                else if (i.input_type === 'document' && i.status === 'completed') c += parseFloat(i.cost || 0);
            });
        } else if (workstage.workstage_inputtype === 'Stage') {
            workstage.stage?.forEach(s => { c += parseFloat(s.completed_work || 0); });
        } else if (workstage.workstage_inputtype === 'Submission') {
            workstage.submission_data?.forEach(s => { if (s.status === 'completed') c += parseFloat(s.cost || 0); });
        }
        return c;
    }, []);

    const calculateSectionStats = useCallback((section) => {
        if (!section?.workstat?.length) return { completedCost:0, totalCost:parseFloat(section?.section_cost||0), completedPercentage:0, pendingPercentage:100, workstageStats:[], totalWorkstages:0 };
        const totalSectionCost = parseFloat(section.section_cost || 0);
        let totalCompletedCost = 0;
        section.workstat.forEach(ws => { totalCompletedCost += calculateWorkstageCompletedCost(ws); });
        const completedPercentage = totalSectionCost > 0 ? Math.min(100, Math.round((totalCompletedCost / totalSectionCost) * 100)) : 0;
        return { completedCost:totalCompletedCost, totalCost:totalSectionCost, completedPercentage, pendingPercentage:100-completedPercentage, totalWorkstages:section.workstat.length };
    }, [calculateWorkstageCompletedCost]);

    const calculateProjectStats = useCallback((project) => {
        if (!project || !dataLoaded) return { completedPercentage:0, totalSections:0, totalProjectCost:0, totalCompletedCost:0, sections:[] };
        const secs = sectionDetailsList.filter(s => s?.project_details === project.id);
        if (!secs.length) return { completedPercentage:0, totalSections:0, totalProjectCost:0, totalCompletedCost:0, sections:[] };
        const projectCost = projectList.reduce((t,i) => t + Number(i.project_post||0), 0);
        let totalProjectCost=0, totalCompletedCost=0;
        const secList = secs.map(sec => { const stats=calculateSectionStats(sec); totalProjectCost+=stats.totalCost; totalCompletedCost+=stats.completedCost; return {...sec,stats}; });
        const completedPercentage = totalProjectCost > 0 ? Math.min(100, Math.round((totalCompletedCost/totalProjectCost)*100)) : 0;
        return { completedPercentage, pendingPercentage:100-completedPercentage, criticalPercentage:completedPercentage<20?100:0, totalSections:secs.length, totalProjectCost, totalprojectCostCard:projectCost, totalCompletedCost, sections:secList };
    }, [sectionDetailsList, dataLoaded, calculateSectionStats, projectList]);

    /* ─── Payment totals for ONE project (same rules as Project Details page) ───
       Input / Stage  → workstage.paymentData
       Submission     → each submission_data[].paymentData                       */
    const calculateProjectPayments = useCallback((project) => {
        const t = { claimed: 0, received: 0, deduction: 0, balanceToClaim: 0, balanceToReceive: 0 };
        if (!project) return t;

        const add = (pd) => {
            if (!pd) return;
            t.claimed          += parseFloat(pd.billingClaimedAmount   || 0);
            t.received         += parseFloat(pd.billingReceivedAmount  || 0);
            t.deduction        += parseFloat(pd.totalDeductionAmount   || 0);
            t.balanceToClaim   += parseFloat(pd.balanceToClaimAmount   || 0);
            t.balanceToReceive += parseFloat(pd.balanceToReceiveAmount || 0);
        };

        sectionDetailsList
            .filter((s) => s?.project_details === project.id)
            .forEach((sec) => {
                sec.workstat?.forEach((ws) => {
                    getPaymentSources(ws).forEach((src) => add(src.pd));
                });
            });

        return t;
    }, [sectionDetailsList]);

    /* ─── Payment totals across ALL visible projects (follows the sector filter) ─── */
    const paymentStats = useMemo(() => {
        const total = { claimed: 0, received: 0, deduction: 0, balanceToClaim: 0, balanceToReceive: 0 };
        if (!dataLoaded) return total;
        projectList.forEach((p) => {
            const x = calculateProjectPayments(p);
            Object.keys(total).forEach((k) => { total[k] += x[k]; });
        });
        return total;
    }, [projectList, dataLoaded, calculateProjectPayments]);

    /* ─── Which section / workstage / submission of a project holds the active payment metric ─── */
    const getProjectPaymentBreakdown = useCallback((project, payKey) => {
        const result = { total: 0, sections: [] };
        if (!project || !payKey) return result;

        sectionDetailsList
            .filter((s) => s?.project_details === project.id)
            .forEach((sec) => {
                const workstages = [];
                let secTotal = 0;

                (sec.workstat || []).forEach((ws) => {
                    const rows = getPaymentSources(ws)
                        .map((src) => ({ label: src.label, value: readPayValue(src.pd, payKey) }))
                        .filter((r) => r.value > 0);

                    const wsTotal = rows.reduce((a, r) => a + r.value, 0);
                    if (wsTotal > 0) {
                        workstages.push({ id: ws.id, name: ws.name || 'Unnamed Stage', type: ws.workstage_inputtype, total: wsTotal, rows });
                        secTotal += wsTotal;
                    }
                });

                if (workstages.length) {
                    result.sections.push({ id: sec.id, name: sec.section_name || 'Unnamed Section', total: secTotal, workstages });
                    result.total += secTotal;
                }
            });

        return result;
    }, [sectionDetailsList]);

    /* ─── filteredAndSortedProjects — status filter + payment filter + sorts ─── */
    const filteredAndSortedProjects = useMemo(() => {
        if (!dataLoaded || !projectList.length) return [];

        // which payment total (if any) the active card is filtering by
        const payKey = activeCardKey ? KPI_FILTER_MAP[activeCardKey]?.paymentKey : undefined;

        let f = projectList.filter(p => {
            const m = (p.project_short_name||'').toLowerCase().includes(searchTerm.toLowerCase()) || (p.pr_code||'').toLowerCase().includes(searchTerm.toLowerCase());
            if (filterStatus === 'all') return m;
            const s = calculateProjectStats(p);
            if (filterStatus === 'completed')   return m && s.completedPercentage > 90;
            if (filterStatus === 'in-progress') return m && s.completedPercentage > 0 && s.completedPercentage < 90;
            if (filterStatus === 'not-started') return m && s.completedPercentage === 0;
            return m;
        });

        // payment card active → keep only projects that actually have a value for that metric
        if (payKey) f = f.filter((p) => calculateProjectPayments(p)[payKey] > 0);

        return f.sort((a,b) => {
            if (sortBy === 'payment' && payKey)
                return calculateProjectPayments(b)[payKey] - calculateProjectPayments(a)[payKey];

            const sa=calculateProjectStats(a), sb=calculateProjectStats(b);
            if (sortBy==='name')     return (a.project_short_name||'').localeCompare(b.project_short_name||'');
            if (sortBy==='progress') return sb.completedPercentage - sa.completedPercentage;
            if (sortBy==='sections') return sb.totalSections - sa.totalSections;
            if (sortBy==='cost')     return Number(b.project_post||0) - Number(a.project_post||0);
            if (sortBy==='billed')   return sb.totalCompletedCost - sa.totalCompletedCost;
            return 0;
        });
    }, [projectList, searchTerm, filterStatus, sortBy, activeCardKey, calculateProjectStats, calculateProjectPayments, dataLoaded]);

    const overallStats = useMemo(() => {
        if (!dataLoaded || !projectList.length || !sectionDetailsList.length)
            return { total:0, completed:0, inProgress:0, notStarted:0, totalCost:0, projectCostCard:0, completedCost:0, overallProgress:0 };
        const projectCost = projectList.reduce((t,i)=>t+Number(i.project_post||0),0);
        let completed=0, inProgress=0, notStarted=0, totalCost=0, completedCost=0;
        projectList.forEach(p => {
            const s=calculateProjectStats(p);
            totalCost+=s.totalProjectCost||0; completedCost+=s.totalCompletedCost||0;
            if (s.completedPercentage > 90) completed++; else if (s.completedPercentage>0) inProgress++; else notStarted++;
        });
        return { total:projectList.length, completed, inProgress, notStarted, totalCost, completedCost, projectCostCard:projectCost,
            overallProgress: projectCost>0 ? Math.round((completedCost/projectCost)*100) : 0 };
    }, [projectList, sectionDetailsList, calculateProjectStats, dataLoaded]);

    const sectorBudgetSplit = useMemo(() => {
        if (!dataLoaded || !projectList.length) return [];
        const map = {};
        projectList.forEach(p => { const n=p.sector_details?.name||'Other'; map[n]=(map[n]||0)+Number(p.project_post||0); });
        return Object.entries(map).map(([name,val])=>({name,val}));
    }, [projectList, dataLoaded]);

    const ganttProjects = useMemo(() => {
        if (!dataLoaded || !filteredAndSortedProjects.length) return [];
        return filteredAndSortedProjects.slice(0,7).map(p => {
            const s=calculateProjectStats(p); const h=getHealth(s.completedPercentage);
            return { name:p.project_short_name||'N/A', pct:s.completedPercentage, color:HEALTH[h].bar };
        });
    }, [filteredAndSortedProjects, calculateProjectStats, dataLoaded]);

    /* ─── Toggles ─── */
    const toggleProject   = id  => setOpenedProjects(p  => p.includes(id)  ? p.filter(x=>x!==id)  : [...p,id]);
    const toggleSection   = id  => setOpenedSections(p  => p.includes(id)  ? p.filter(x=>x!==id)  : [...p,id]);
    const toggleWorkstage = (sId,wId) => { const k=`${sId}-${wId}`; setOpenedWorkstages(p=>p.includes(k)?p.filter(x=>x!==k):[...p,k]); };

    const toggleExpandAll = () => {
        if (!dataLoaded || !filteredAndSortedProjects.length) return;
        if (expandedAll) { setOpenedProjects([]); setOpenedSections([]); setOpenedWorkstages([]); }
        else {
            setOpenedProjects(filteredAndSortedProjects.map(p=>p.id).filter(Boolean));
            setOpenedSections(sectionDetailsList.map(s=>s.id).filter(Boolean));
            setOpenedWorkstages(sectionDetailsList.flatMap(s=>(s.workstat||[]).map(ws=>`${s.id}-${ws.id}`)).filter(Boolean));
        }
        setExpandedAll(!expandedAll);
    };

    const handleRefresh = () => setRefreshTrigger(p=>p+1);
    const location = useLocation();
    useEffect(() => {
        if (location.state?.refresh) { setRefreshTrigger(p=>p+1); navigate(location.pathname, { replace:true, state:{} }); }
    }, [location.state, navigate]);
    useEffect(() => {
        const h=e=>{ if(e.key==='refreshDashboard'||e.key==='sectionUpdated') setRefreshTrigger(p=>p+1); };
        window.addEventListener('storage',h); return ()=>window.removeEventListener('storage',h);
    }, []);
    useEffect(() => { window.refreshDashboard=()=>setRefreshTrigger(p=>p+1); return ()=>{ delete window.refreshDashboard; }; }, []);

    /* ─── Payment audit (run  auditPayments()  in the browser console) ───
       Lists every payment record behind the 5 cards, flags records whose
       numbers don't add up, and shows how much the totals changed versus
       the old "submission-level only" rule. */
    useEffect(() => {
        window.auditPayments = () => {
            const tol = 1; // ₹1 rounding tolerance
            const f = (v) => parseFloat(v || 0) || 0;
            const rows = [];
            const oldT = { claimed: 0, received: 0, deduction: 0, balanceToClaim: 0, balanceToReceive: 0 };

            projectList.forEach((p) => {
                sectionDetailsList.filter((s) => s?.project_details === p.id).forEach((sec) => {
                    (sec.workstat || []).forEach((ws) => {
                        // totals under the OLD rule (Submission → submission-level only)
                        const oldPds = ws.workstage_inputtype === 'Submission'
                            ? (ws.submission_data || []).map((x) => x.paymentData)
                            : [ws.paymentData];
                        oldPds.forEach((pd) => Object.keys(oldT).forEach((k) => { oldT[k] += readPayValue(pd, k); }));

                        // records counted under the NEW rule
                        getPaymentSources(ws).forEach((src) => {
                            const pd = src.pd;
                            const claimed = readPayValue(pd, 'claimed');
                            const received = readPayValue(pd, 'received');
                            const deduction = readPayValue(pd, 'deduction');
                            const balClaim = readPayValue(pd, 'balanceToClaim');
                            const balRecv = readPayValue(pd, 'balanceToReceive');
                            const aboutSaved = f(pd.aboutToBillAmount);
                            const aboutNow = calculateWorkstageCompletedCost(ws);
                            const net = f(pd.netReceivedAmount);

                            const issues = [];
                            if (Math.abs(claimed + balClaim - aboutSaved) > tol) issues.push('claimed + bal.to claim ≠ about-to-bill');
                            if (Math.abs(claimed - received - balRecv) > tol) issues.push('claimed − received ≠ bal.to receive');
                            if (Math.abs(received - deduction - net) > tol) issues.push('received − deduction ≠ net received');
                            if (received > claimed + tol) issues.push('received > claimed');
                            if (Math.abs(aboutSaved - aboutNow) > tol) issues.push('STALE: work changed after payment was saved');

                            rows.push({
                                project: p.pr_code, section: sec.section_name, workstage: ws.name,
                                type: ws.workstage_inputtype, source: src.label || 'workstage',
                                claimed, received, deduction, balClaim, balRecv, aboutSaved, aboutNow,
                                issues: issues.join(' | '),
                            });
                        });
                    });
                });
            });

            const newT = rows.reduce((a, r) => ({
                claimed: a.claimed + r.claimed, received: a.received + r.received, deduction: a.deduction + r.deduction,
                balanceToClaim: a.balanceToClaim + r.balClaim, balanceToReceive: a.balanceToReceive + r.balRecv,
            }), { claimed: 0, received: 0, deduction: 0, balanceToClaim: 0, balanceToReceive: 0 });

            console.table(rows);
            console.log('TOTALS now (must equal the 5 cards, exact ₹):', newT);
            console.log('TOTALS with the OLD rule:', oldT);
            console.log('Difference caused by the fix:', Object.fromEntries(Object.keys(newT).map((k) => [k, Number((newT[k] - oldT[k]).toFixed(2))])));
            console.log(`Records: ${rows.length} · with issues: ${rows.filter((r) => r.issues).length} · stale: ${rows.filter((r) => r.issues.includes('STALE')).length}`);
            return rows;
        };
        return () => { delete window.auditPayments; };
    }, [projectList, sectionDetailsList, calculateWorkstageCompletedCost]);

    /* ─── Sector buttons (IDs come from config/sectors.js) ─── */
    const SECTOR_BUTTONS = [
        { key: 'Railway', label: 'Railways', Icon: RailwaySvg },
        { key: 'Highway', label: 'Highways', Icon: HighwaySvg },
        { key: 'Ropeway', label: 'Ropeways', Icon: RopewaySvg },
    ];

    const SectorButtons = () => (
        <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }}>
            <button onClick={()=>setToggleSector('null')} style={{ ...pillStyle, background:toggleSector==='null'?'#185FA5':'#fff', color:toggleSector==='null'?'#fff':'#555', borderColor:toggleSector==='null'?'#185FA5':'#e2e8f0' }}>All</button>
            {SECTOR_BUTTONS.map(({ key, label, Icon }) => {
                const active = toggleSector === SECTORS[key].id;
                return (
                    <button key={key} onClick={()=>setToggleSector(SECTORS[key].id)} style={{ ...pillStyle, background:active?'#185FA5':'#fff', color:active?'#fff':'#555', borderColor:active?'#185FA5':'#e2e8f0' }}>
                        <Icon active={active}/> {label}
                    </button>
                );
            })}
        </div>
    );

    /* ════════════════════════════════
       LOADING → show skeletons
    ════════════════════════════════ */
    if (loading) {
        return (
            <>
                <Dashboardnavbar name="Project Portfolio" url={"Project Portfolio"} />
                <div style={styles.page}>
                    <motion.div style={styles.topBar} initial={{ opacity:0, y:-12 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.4 }}>
                        <div><div style={styles.pageSub}><span className="ehd-live-dot"/>Fetching data…</div></div>
                        <div style={{ display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
                            <SectorButtons/>
                            <AddProjectName getProjectname={getProjectData} useFor="dashboard"/>
                            <button style={styles.iconBtn} onClick={handleRefresh}><RefreshCw size={16}/></button>
                        </div>
                    </motion.div>
                    <div style={styles.kpiRow}>
                        {[0,1,2,3,4,5].map(i=>(
                            <motion.div key={i} initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay:i*0.06, duration:0.4 }}>
                                <SkeletonKpiCard/>
                            </motion.div>
                        ))}
                    </div>
                    <div style={styles.kpiRow}>
                        {[0,1,2,3,4].map(i=>(
                            <motion.div key={i} initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.3+i*0.06, duration:0.4 }}>
                                <SkeletonKpiCard/>
                            </motion.div>
                        ))}
                    </div>
                    <div style={styles.progressCard}>
                        <Skeleton w="35%" h={14} r={5} style={{ marginBottom:12 }}/>
                        <Skeleton w="100%" h={10} r={5}/>
                    </div>
                    <div style={styles.analyticsRow}>
                        {[0,1,2].map(i=>(
                            <motion.div key={i} style={styles.analyticsCard} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:0.2+i*0.1 }}>
                                <Skeleton w="50%" h={13} r={4} style={{ marginBottom:16 }}/>
                                {[0,1,2,3].map(j=><Skeleton key={j} w="100%" h={8} r={4} style={{ marginBottom:10 }}/>)}
                            </motion.div>
                        ))}
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                        {[0,1,2,3].map(i=><SkeletonProjectCard key={i} index={i}/>)}
                    </div>
                    <style>{globalCSS}</style>
                </div>
            </>
        );
    }

    /* ── KPI card definitions ── */
    const kpiCards = [
        {
            cardKey: 'total',
            label: 'Total Projects',
            rawValue: overallStats.total,
            displayValue: overallStats.total,
            sub: `${overallStats.inProgress} active`,
            color: '#185FA5',
            icon: <Folder size={22}/>,
        },
        {
            cardKey: 'completed',
            label: 'Completed',
            rawValue: overallStats.completed,
            displayValue: overallStats.completed,
            sub: `${overallStats.total>0?Math.round(overallStats.completed/overallStats.total*100):0}% rate`,
            color: '#3B6D11',
            icon: <CheckCircle size={22}/>,
        },
        {
            cardKey: 'inProgress',
            label: 'In Progress',
            rawValue: overallStats.inProgress,
            displayValue: overallStats.inProgress,
            sub: 'Active projects',
            color: '#BA7517',
            icon: <TrendingUp size={22}/>,
        },
        {
            cardKey: 'notStarted',
            label: 'Not Started',
            rawValue: overallStats.notStarted,
            displayValue: overallStats.notStarted,
            sub: 'Awaiting kickoff',
            color: '#A32D2D',
            icon: <AlertCircle size={22}/>,
        },
        {
            cardKey: 'totalBudget',
            label: 'Total Budget',
            rawValue: null,
            displayValue: `₹${formatCurrencyIndian(Number((overallStats.projectCostCard/10000000).toFixed(2)))} Cr`,
            sub: 'Across all projects',
            color: '#0F6E56',
            icon: <IndianRupee size={22}/>,
        },
        {
            cardKey: 'aboutToBill',
            label: 'To Be Billed',
            rawValue: null,
            displayValue: `₹${formatCurrencyIndian(Number((overallStats.completedCost/10000000).toFixed(2)))} Cr`,
            sub: `${overallStats.overallProgress}% of budget`,
            color: '#534AB7',
            icon: <IndianRupee size={22}/>,
        },
    ];

    /* ── Active payment card (null when a non-payment card / no card is active) ── */
    const activePayKey   = activeCardKey ? KPI_FILTER_MAP[activeCardKey]?.paymentKey : undefined;
    const activePayColor = activeCardKey ? (CARD_COLOR[activeCardKey] || '#185FA5') : '#185FA5';

    /* ── Payment summary card definitions ── */
    const fmtMoney = (v) =>
        v >= 10000000
            ? `₹${formatCurrencyIndian(Number((v / 10000000).toFixed(2)))} Cr`
            : `₹${formatCurrencyIndian(Number((v / 100000).toFixed(2)))} Lacs`;

    const paymentCards = [
        { cardKey: 'pClaimed',   label: 'Claimed',         rawValue: null, displayValue: fmtMoney(paymentStats.claimed),          sub: 'total billed to client', color: '#185FA5', icon: <IndianRupee size={22}/> },
        { cardKey: 'pReceived',  label: 'Received',        rawValue: null, displayValue: fmtMoney(paymentStats.received),         sub: 'payment collected',      color: '#1D9E75', icon: <IndianRupee size={22}/> },
        { cardKey: 'pDeduction', label: 'Deduction',       rawValue: null, displayValue: fmtMoney(paymentStats.deduction),        sub: 'TDS + retention',        color: '#E24B4A', icon: <IndianRupee size={22}/> },
        { cardKey: 'pBalClaim',  label: 'Bal. to Claim',   rawValue: null, displayValue: fmtMoney(paymentStats.balanceToClaim),   sub: 'yet to be claimed',      color: '#EF9F27', icon: <IndianRupee size={22}/> },
        { cardKey: 'pBalRecv',   label: 'Bal. to Receive', rawValue: null, displayValue: fmtMoney(paymentStats.balanceToReceive), sub: 'pending receipt',        color: '#7C3AED', icon: <IndianRupee size={22}/> },
    ];

    /* ════════════════════════════════
       MAIN RENDER
    ════════════════════════════════ */
    return (
        <>
            <Dashboardnavbar name="Project Portfolio" url={"Project Portfolio"}/>
            <div style={styles.page}>
                <style>{globalCSS}</style>

                {/* ── Top bar ── */}
                <motion.div style={styles.topBar} variants={fadeUp} custom={0} initial="hidden" animate="visible">
                    <div>
                        <div style={styles.pageSub}><span className="ehd-live-dot"/>Cost-based progress · {filteredAndSortedProjects.length} projects</div>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
                        <SectorButtons/>
                        <AddProjectName getProjectname={getProjectData} useFor="dashboard"/>
                        <motion.button style={styles.iconBtn} onClick={handleRefresh}
                            whileHover={{ rotate:180 }} transition={{ duration:0.4 }}>
                            <RefreshCw size={16}/>
                        </motion.button>
                    </div>
                </motion.div>

                {/* ══ KPI Cards — all clickable ══ */}
                <div style={styles.kpiRow}>
                    {kpiCards.map((k, i) => (
                        <KpiCard
                            key={k.cardKey}
                            {...k}
                            delay={i}
                            activeCardKey={activeCardKey}
                            onCardClick={handleCardClick}
                        />
                    ))}
                </div>

                {/* ══ Payment Summary cards — clickable, filter + sort the project list ══ */}
                <div style={styles.sectionLabel}>Payment Summary</div>
                <div style={styles.kpiRow}>
                    {paymentCards.map((k, i) => (
                        <KpiCard
                            key={k.cardKey}
                            {...k}
                            delay={i}
                            activeCardKey={activeCardKey}
                            onCardClick={handleCardClick}
                        />
                    ))}
                </div>

                {/* ══ Active filter banner ══ */}
                <ActiveFilterBanner
                    activeCardKey={activeCardKey}
                    cardLabel={activeCardKey ? KPI_FILTER_MAP[activeCardKey]?.label : ''}
                    color={activeCardKey ? CARD_COLOR[activeCardKey] : '#185FA5'}
                    count={filteredAndSortedProjects.length}
                    onClear={handleClearCardFilter}
                />

                {/* ══ Overall progress bar ══ */}
                <motion.div style={styles.progressCard} variants={fadeUp} custom={1} initial="hidden" animate="visible">
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px', flexWrap:'wrap', gap:'8px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                            <span style={{ fontSize:'14px', fontWeight:600, color:'#0f172a' }}>Overall Portfolio Progress</span>
                            <motion.span style={{ fontSize:'22px', fontWeight:700, color:'#185FA5' }}
                                initial={{ opacity:0, scale:0.5 }} animate={{ opacity:1, scale:1 }} transition={{ duration:0.5, delay:0.3, type:'spring', stiffness:200 }}>
                                {overallStats.overallProgress}%
                            </motion.span>
                        </div>
                        <div style={{ display:'flex', gap:'18px', fontSize:'12px', flexWrap:'wrap' }}>
                            <span style={{ color:'#64748b' }}>Total: <b style={{ color:'#0f172a' }}>₹{formatCurrencyIndian((overallStats.projectCostCard/100000).toFixed(2))} Lacs</b></span>
                            <span style={{ color:'#3B6D11' }}>To Be Billed: <b>₹{formatCurrencyIndian((overallStats.completedCost/100000).toFixed(2))} Lacs</b></span>
                            <span style={{ color:'#A32D2D' }}>Remaining: <b>₹{formatCurrencyIndian(((overallStats.projectCostCard-overallStats.completedCost)/100000).toFixed(2))} Lacs</b></span>
                        </div>
                    </div>
                    <AnimBar pct={overallStats.overallProgress} color="linear-gradient(90deg,#185FA5,#4a9eff)" height={10} radius={5} delay={0.4}/>
                </motion.div>

                {/* ══ Analytics row ══ */}
                <div style={styles.analyticsRow}>
                    {/* Donut */}
                    <motion.div style={styles.analyticsCard} variants={fadeUp} custom={2} initial="hidden" animate="visible">
                        <div style={styles.analyticsTitle}>Status Distribution</div>
                        <div style={{ display:'flex', alignItems:'center', gap:'20px' }}>
                            {(() => {
                                const total = overallStats.total || 1;
                                const R = 36, C = 2*Math.PI*R;
                                const slices=[
                                    { label:'Completed',   count:overallStats.completed,  color:'#3B6D11', cardKey:'completed' },
                                    { label:'In Progress', count:overallStats.inProgress,  color:'#185FA5', cardKey:'inProgress' },
                                    { label:'Not Started', count:overallStats.notStarted,  color:'#E24B4A', cardKey:'notStarted' },
                                ];
                                let cum=0;
                                return (
                                    <div style={{ position:'relative', flexShrink:0 }}>
                                        <svg width="90" height="90" viewBox="0 0 90 90">
                                            <circle cx="45" cy="45" r={R} fill="none" stroke="#f1f5f9" strokeWidth="10"/>
                                            {slices.map((s,i)=>{
                                                const dashLen=(s.count/total)*C;
                                                const offset=-(cum/total)*C-C*0.25;
                                                cum+=s.count;
                                                return (
                                                    <motion.circle key={i} cx="45" cy="45" r={R} fill="none"
                                                        stroke={s.color} strokeWidth="10" strokeLinecap="round"
                                                        initial={{ strokeDasharray:`0 ${C}` }}
                                                        animate={{ strokeDasharray:`${dashLen} ${C}` }}
                                                        transition={{ duration:1.2, delay:0.3+i*0.15, ease:[0.22,1,0.36,1] }}
                                                        strokeDashoffset={offset}
                                                        style={{ cursor:'pointer' }}
                                                        onClick={()=>handleCardClick(s.cardKey)}
                                                    />
                                                );
                                            })}
                                        </svg>
                                        <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
                                            <span style={{ fontSize:'18px', fontWeight:700, color:'#0f172a', lineHeight:1 }}>{total}</span>
                                            <span style={{ fontSize:'9px', color:'#94a3b8' }}>projects</span>
                                        </div>
                                    </div>
                                );
                            })()}
                            <div style={{ flex:1, display:'flex', flexDirection:'column', gap:8 }}>
                                {[{ label:'Completed',   count:overallStats.completed,  color:'#3B6D11', cardKey:'completed' },
                                  { label:'In Progress', count:overallStats.inProgress,  color:'#185FA5', cardKey:'inProgress' },
                                  { label:'Not Started', count:overallStats.notStarted,  color:'#E24B4A', cardKey:'notStarted' }]
                                .map((s,i) => (
                                    <motion.div key={s.label}
                                        style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', borderRadius:6, padding:'2px 4px',
                                            background: activeCardKey === s.cardKey ? `${s.color}15` : 'transparent',
                                            transition: 'background 0.2s' }}
                                        onClick={()=>handleCardClick(s.cardKey)}
                                        initial={{ opacity:0, x:-12 }} animate={{ opacity:1, x:0 }} transition={{ delay:0.4+i*0.1 }}
                                        whileHover={{ backgroundColor:`${s.color}10` }}>
                                        <div style={{ width:10, height:10, borderRadius:'50%', background:s.color, flexShrink:0 }}/>
                                        <span style={{ fontSize:'12px', color:'#64748b', flex:1 }}>{s.label}</span>
                                        <span style={{ fontSize:'13px', fontWeight:700, color:s.color }}>{s.count}</span>
                                    </motion.div>
                                ))}
                                <div style={{ borderTop:'1px solid #f1f5f9', paddingTop:8, marginTop:4, display:'flex', alignItems:'center', gap:8 }}>
                                    <span style={{ fontSize:'11px', color:'#94a3b8' }}>Behind schedule</span>
                                    <span style={{ fontSize:'12px', fontWeight:700, color:'#E24B4A' }}>
                                        {filteredAndSortedProjects.filter(p=>{ const s=calculateProjectStats(p); return s.completedPercentage>0&&s.completedPercentage<20; }).length}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Sector split */}
                    <motion.div style={styles.analyticsCard} variants={fadeUp} custom={3} initial="hidden" animate="visible">
                        <div style={styles.analyticsTitle}>Sector Budget Split</div>
                        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                            {sectorBudgetSplit.map((s,i) => {
                                const total=sectorBudgetSplit.reduce((a,x)=>a+x.val,0)||1;
                                const pct=Math.round((s.val/total)*100);
                                const colors=['#185FA5','#BA7517','#3B6D11','#534AB7','#0F6E56'];
                                const col=colors[i%colors.length];
                                return (
                                    <motion.div key={s.name} initial={{ opacity:0, x:20 }} animate={{ opacity:1, x:0 }} transition={{ delay:0.3+i*0.1 }}>
                                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                                            <span style={{ fontSize:'12px', color:'#334155', fontWeight:500 }}>{s.name}</span>
                                            <span style={{ fontSize:'12px', fontWeight:700, color:col }}>₹{formatCurrencyIndian((s.val/100000).toFixed(2))} L</span>
                                        </div>
                                        <AnimBar pct={pct} color={col} height={6} delay={0.4+i*0.1}/>
                                    </motion.div>
                                );
                            })}
                            <div style={{ marginTop:8, borderTop:'1px solid #f1f5f9', paddingTop:12 }}>
                                <div style={{ fontSize:'12px', fontWeight:600, color:'#64748b', marginBottom:8 }}>Section cost vs billed</div>
                                <div style={{ display:'flex', gap:10 }}>
                                    {[{ label:'Section cost', val:`₹${formatCurrencyIndian((overallStats.totalCost/100000).toFixed(2))} Lacs`, color:'#185FA5', cardKey:'totalBudget' },
                                      { label:'About to bill', val:`₹${formatCurrencyIndian((overallStats.completedCost/100000).toFixed(2))} Lacs`, color:'#3B6D11', cardKey:'aboutToBill' }]
                                    .map((x,i)=>(
                                        <motion.div key={x.label}
                                            style={{ flex:1, background: activeCardKey===x.cardKey ? `${x.color}15` : '#f8fafc', borderRadius:8, padding:'8px 12px', textAlign:'center',
                                                cursor:'pointer', border:`1px solid ${activeCardKey===x.cardKey ? x.color+'50' : 'transparent'}`, transition:'all 0.2s' }}
                                            onClick={()=>handleCardClick(x.cardKey)}
                                            initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.6+i*0.1 }}
                                            whileHover={{ scale:1.02 }}>
                                            <div style={{ fontSize:'10px', color:'#94a3b8', marginBottom:3 }}>{x.label}</div>
                                            <div style={{ fontSize:'15px', fontWeight:700, color:x.color }}>{x.val}</div>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Gantt */}
                    <motion.div style={styles.analyticsCard} variants={fadeUp} custom={4} initial="hidden" animate="visible">
                        <div style={styles.analyticsTitle}>Project Timeline (Gantt)</div>
                        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                            {ganttProjects.length===0 ? (
                                <div style={{ fontSize:'12px', color:'#94a3b8' }}>No projects to display</div>
                            ) : ganttProjects.map((p,i)=>(
                                <motion.div key={i} style={{ display:'flex', alignItems:'center', gap:10 }}
                                    initial={{ opacity:0, x:-16 }} animate={{ opacity:1, x:0 }} transition={{ delay:0.2+i*0.08 }}>
                                    <span style={{ fontSize:'11px', color:'#334155', width:100, flexShrink:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name}</span>
                                    <div style={{ flex:1 }}>
                                        <AnimBar pct={p.pct} color={p.color} height={8} radius={4} delay={0.3+i*0.08}/>
                                    </div>
                                    <span style={{ fontSize:'11px', fontWeight:700, color:p.color, width:30, textAlign:'right', flexShrink:0 }}>{p.pct}%</span>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>

                {/* ══ Controls ══ */}
                <motion.div style={styles.controls} variants={fadeUp} custom={5} initial="hidden" animate="visible">
                    <div style={styles.searchWrap}>
                        <Search size={15} color="#94a3b8"/>
                        <input style={styles.searchInput} type="text" placeholder="Search projects or codes…"
                            value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} disabled={!dataLoaded}/>
                        {searchTerm && (
                            <motion.button initial={{ scale:0 }} animate={{ scale:1 }} onClick={()=>setSearchTerm('')}
                                style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8', display:'flex', padding:0 }}>
                                <X size={14}/>
                            </motion.button>
                        )}
                    </div>
                    <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
                        <button style={styles.ctrlBtn} onClick={()=>dataLoaded&&setShowFilters(!showFilters)}><Filter size={14}/> Filter</button>
                        <select style={styles.selectEl} value={sortBy}
                            onChange={e=>{ setSortBy(e.target.value); setActiveCardKey(null); }}
                            disabled={!dataLoaded}>
                            <option value="progress">Sort: Progress</option>
                            <option value="name">Sort: Name</option>
                            <option value="sections">Sort: Sections</option>
                            <option value="cost">Sort: Budget</option>
                            <option value="billed">Sort: Billed</option>
                            <option value="payment" disabled>Sort: Payment (via card)</option>
                        </select>
                        <button style={{ ...styles.ctrlBtn, background:expandedAll?'#185FA5':'#fff', color:expandedAll?'#fff':'#374151', borderColor:expandedAll?'#185FA5':'#e2e8f0' }}
                            onClick={toggleExpandAll} disabled={!dataLoaded||!filteredAndSortedProjects.length}>
                            {expandedAll?'Collapse All':'Expand All'}
                        </button>
                    </div>
                </motion.div>

                {/* filter chips */}
                <AnimatePresence>
                    {showFilters && dataLoaded && (
                        <motion.div initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:'auto' }} exit={{ opacity:0, height:0 }}
                            style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                            {['all','completed','in-progress','not-started'].map(s=>(
                                <motion.button key={s} onClick={()=>{ setFilterStatus(s); setActiveCardKey(null); }}
                                    whileHover={{ scale:1.04 }} whileTap={{ scale:0.96 }}
                                    style={{ padding:'5px 14px', borderRadius:'20px', fontSize:'12px', cursor:'pointer', border:'1px solid', fontWeight:500,
                                        background:filterStatus===s&&!activeCardKey?'#185FA5':'#fff',
                                        color:filterStatus===s&&!activeCardKey?'#fff':'#374151',
                                        borderColor:filterStatus===s&&!activeCardKey?'#185FA5':'#e2e8f0' }}>
                                    {s.replace('-',' ').toUpperCase()}
                                </motion.button>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ══ Project cards ══ */}
                <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
                    {!dataLoaded ? (
                        [0,1,2].map(i=><SkeletonProjectCard key={i} index={i}/>)
                    ) : filteredAndSortedProjects.length===0 ? (
                        <motion.div style={{ textAlign:'center', padding:'60px 20px', color:'#94a3b8' }}
                            initial={{ opacity:0 }} animate={{ opacity:1 }}>
                            <FileText size={48} style={{ marginBottom:'12px', opacity:.4 }}/>
                            <div style={{ fontSize:'16px', fontWeight:600, color:'#64748b' }}>No Projects Found</div>
                            <div style={{ fontSize:'13px', marginTop:'6px' }}>
                                {activeCardKey
                                    ? <span>No projects match <strong>{KPI_FILTER_MAP[activeCardKey]?.label}</strong>. <button onClick={handleClearCardFilter} style={{ background:'none', border:'none', cursor:'pointer', color:'#185FA5', fontWeight:600, fontSize:'13px' }}>Clear filter</button></span>
                                    : searchTerm ? 'Try adjusting your search' : 'Start by creating your first project'}
                            </div>
                        </motion.div>
                    ) : (
                        <AnimatePresence>
                            {filteredAndSortedProjects.map((project, index) => {
                                const pStats  = calculateProjectStats(project);
                                const health  = getHealth(pStats.completedPercentage);
                                const hStyle  = HEALTH[health];
                                const isOpen  = openedProjects.includes(project.id);
                                const sections = pStats.sections || [];

                                return (
                                    <motion.div key={project.id}
                                        variants={fadeUp} custom={index}
                                        initial="hidden" animate="visible"
                                        exit={{ opacity:0, y:-12, transition:{ duration:0.2 } }}
                                        layout
                                        style={styles.projCard}
                                        whileHover={{ boxShadow:'0 4px 20px rgba(0,0,0,0.08)', transition:{ duration:0.2 } }}>

                                        {/* project header */}
                                        <motion.div
                                            style={{ ...styles.projHeader, background:isOpen?'#f8fafc':'#fff' }}
                                            onClick={()=>dataLoaded&&toggleProject(project.id)}
                                            whileHover={{ backgroundColor:'#f8fafc' }}>

                                            <motion.div style={{ width:40, height:40, borderRadius:10, background:hStyle.folder, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}
                                                whileHover={{ scale:1.1, rotate:isOpen?-5:5 }} transition={{ type:'spring', stiffness:400, damping:20 }}>
                                                <AnimatePresence mode="wait">
                                                    <motion.div key={isOpen?'open':'closed'}
                                                        initial={{ scale:0, rotate:-10 }} animate={{ scale:1, rotate:0 }} exit={{ scale:0 }} transition={{ duration:0.2 }}>
                                                        {isOpen ? <FolderOpen size={20} color={hStyle.icon}/> : <Folder size={20} color={hStyle.icon}/>}
                                                    </motion.div>
                                                </AnimatePresence>
                                            </motion.div>

                                            <div style={{ flex:1, minWidth:0 }}>
                                                <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'5px', flexWrap:'wrap' }}>
                                                    <span style={{ fontSize:'14px', fontWeight:600, color:'#0f172a', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                                        {project.project_short_name||'Unnamed Project'}
                                                    </span>
                                                    <span style={styles.codeChip}>{project.pr_code||'N/A'}</span>
                                                    <motion.span style={{ ...styles.statusChip, ...chipColors[health] }}
                                                        initial={{ scale:0 }} animate={{ scale:1 }} transition={{ type:'spring', stiffness:400, delay:index*0.05+0.2 }}>
                                                        {BADGE_LABEL[health]}
                                                    </motion.span>
                                                </div>
                                                <div style={{ display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap' }}>
                                                    <MetaItem icon={<Layers size={12}/>}       label={`${pStats.totalSections} Sections`}/>
                                                    <MetaItem icon={<Construction size={12}/>}  label={project.sector_details?.name||'N/A'}/>
                                                    <MetaItem icon={<Calendar size={12}/>}      label={`${project.loa_date?new Date(project.loa_date).getFullYear():'N/A'} – ${project.completion_date?new Date(project.completion_date).getFullYear():'N/A'}`}/>
                                                    <span style={{ fontSize:'12px', fontWeight:600, color:hStyle.icon }}>₹{formatCurrencyIndian(Number((project.project_post/100000).toFixed(2)))} Lacs</span>
                                                </div>
                                            </div>

                                            <div style={{ display:'flex', alignItems:'center', gap:'10px', flexShrink:0 }}>
                                                <div style={{ textAlign:'right' }}>
                                                    <motion.div style={{ fontSize:'20px', fontWeight:700, color:hStyle.ring, lineHeight:1 }}
                                                        initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }} transition={{ delay:index*0.05+0.3 }}>
                                                        {pStats.completedPercentage}%
                                                    </motion.div>
                                                    <div style={{ fontSize:'10px', color:'#94a3b8' }}>complete</div>
                                                </div>
                                                <AnimRing pct={pStats.completedPercentage} color={hStyle.ring} bg={hStyle.folder} size={44} strokeWidth={5} fontSize="9px" delay={index*0.06+0.2}/>
                                                <motion.div style={{ color:'#94a3b8' }}
                                                    animate={{ rotate:isOpen?90:0 }} transition={{ type:'spring', stiffness:300, damping:25 }}>
                                                    <ChevronRight size={18}/>
                                                </motion.div>
                                            </div>
                                        </motion.div>

                                        {/* progress bar strip */}
                                        <div style={{ padding:'0 16px 12px' }}>
                                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'5px', flexWrap:'wrap', gap:'4px' }}>
                                                <span style={{ fontSize:'11px', color:'#94a3b8' }}>Progress</span>
                                                <div style={{ display:'flex', gap:'14px', fontSize:'11px', flexWrap:'wrap' }}>
                                                    <span style={{ color:'#185FA5', fontWeight:600 }}>Total: ₹{formatCurrencyIndian(Number((pStats.totalProjectCost/100000).toFixed(2)))} Lacs</span>
                                                    <span style={{ color:'#3B6D11', fontWeight:600 }}>Billed: ₹{formatCurrencyIndian(Number((pStats.totalCompletedCost/100000).toFixed(2)))} Lacs</span>
                                                    <span style={{ color:'#A32D2D', fontWeight:600 }}>Remaining: ₹{formatCurrencyIndian(Number(((pStats.totalProjectCost-pStats.totalCompletedCost)/100000).toFixed(2)))} Lacs</span>
                                                </div>
                                            </div>
                                            <AnimBar pct={pStats.completedPercentage} color={hStyle.bar} height={6} delay={index*0.05+0.15}/>
                                        </div>

                                        {/* payment summary of THIS project (always visible) */}
                                        <ProjectPaymentSummary
                                            pay={calculateProjectPayments(project)}
                                            activeKey={activePayKey}
                                        />

                                        {/* payment breakdown — only while a payment card is active */}
                                        {activePayKey && (
                                            <PaymentBreakdown
                                                breakdown={getProjectPaymentBreakdown(project, activePayKey)}
                                                label={paymentCards.find((c) => c.cardKey === activeCardKey)?.label || ''}
                                                color={activePayColor}
                                            />
                                        )}

                                        {/* expanded body */}
                                        <AnimatePresence>
                                            {isOpen && (
                                                <motion.div initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:'auto' }}
                                                    exit={{ opacity:0, height:0 }} transition={{ duration:0.3, ease:[0.22,1,0.36,1] }}
                                                    style={{ borderTop:'1px solid #f1f5f9', overflow:'hidden' }}>
                                                    <div style={{ padding:'16px' }}>
                                                        <div style={styles.detailGrid}>
                                                            {[
                                                                { icon:<BarChart3 size={16}/>, label:'Department',  val:project.department||'N/A' },
                                                                { icon:<MapPin size={16}/>,    label:'Total Length', val:`${project.total_length||'0'} KM` },
                                                                { icon:<WrenchIcon size={16}/>, label:'Work Type',  val:project.worktype_details?.name||'N/A' },
                                                                { icon:<Building size={16}/>,  label:'Division',    val:project.division_name||'N/A' },
                                                            ].map((d,i)=>(
                                                                <motion.div key={d.label} style={styles.detailItem}
                                                                    initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ delay:i*0.07 }}>
                                                                    <div style={{ color:'#94a3b8', marginBottom:'4px' }}>{d.icon}</div>
                                                                    <div style={{ fontSize:'10px', color:'#94a3b8', marginBottom:'2px' }}>{d.label}</div>
                                                                    <div style={{ fontSize:'13px', fontWeight:600, color:'#0f172a' }}>{d.val}</div>
                                                                </motion.div>
                                                            ))}
                                                        </div>

                                                        <div style={{ fontSize:'13px', fontWeight:600, color:'#0f172a', marginBottom:'10px' }}>Project Sections</div>

                                                        {sections.length===0 ? (
                                                            <div style={{ fontSize:'13px', color:'#94a3b8', padding:'12px 0' }}>No sections available</div>
                                                        ) : (
                                                            <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                                                                {sections.map((section, si) => {
                                                                    const sStats  = section.stats;
                                                                    const isSOpen = openedSections.includes(section.id);
                                                                    const accentColors=['#185FA5','#3B6D11','#BA7517','#534AB7','#0F6E56','#A32D2D'];
                                                                    const accent=accentColors[si%accentColors.length];

                                                                    return (
                                                                        <motion.div key={section.id}
                                                                            initial={{ opacity:0, x:-10 }} animate={{ opacity:1, x:0 }} transition={{ delay:si*0.07 }}
                                                                            style={{ borderRadius:10, border:'1px solid #e2e8f0', overflow:'hidden', borderLeft:`4px solid ${accent}` }}>

                                                                            <motion.div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', cursor:'pointer', background:isSOpen?'#f8fafc':'#fff' }}
                                                                                onClick={()=>toggleSection(section.id)}
                                                                                whileHover={{ backgroundColor:'#f8fafc' }}>
                                                                                <div style={{ flex:1, minWidth:0 }}>
                                                                                    <div style={{ fontSize:'13px', fontWeight:600, color:'#0f172a', marginBottom:'3px' }}>{section.section_name||'Unnamed Section'}</div>
                                                                                    <div style={{ display:'flex', gap:10, fontSize:'11px', color:'#94a3b8', flexWrap:'wrap' }}>
                                                                                        <span><MapPin size={10} style={{ display:'inline', marginRight:2 }}/>{section.location||'N/A'}</span>
                                                                                        <span>Length: {section.length||'0'} Km</span>
                                                                                        <span>Stages: {section.workstat?.length||0}</span>
                                                                                        <span style={{ color:accent, fontWeight:600 }}>₹{formatCurrencyIndian(Number((sStats.totalCost/100000).toFixed(2)))} Lacs</span>
                                                                                    </div>
                                                                                </div>
                                                                                <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
                                                                                    <motion.button style={styles.eyeBtn} whileHover={{ scale:1.1 }} whileTap={{ scale:0.9 }}
                                                                                        onClick={e=>{ e.stopPropagation(); ViewAllProjectDetails(section); }}>
                                                                                        <Eye size={14}/>
                                                                                    </motion.button>
                                                                                    <AnimRing pct={sStats.completedPercentage} color={accent} bg="#f1f5f9" size={38} strokeWidth={4} fontSize="9px" delay={si*0.1+0.2}/>
                                                                                    <motion.div style={{ color:'#94a3b8' }}
                                                                                        animate={{ rotate:isSOpen?90:0 }} transition={{ type:'spring', stiffness:300, damping:25 }}>
                                                                                        <ChevronRight size={16}/>
                                                                                    </motion.div>
                                                                                </div>
                                                                            </motion.div>

                                                                            <div style={{ padding:'4px 14px 10px', display:'flex', gap:14, fontSize:'11px', flexWrap:'wrap' }}>
                                                                                <span style={{ color:'#185FA5', fontWeight:600 }}>Total: ₹{formatCurrencyIndian(Number((sStats.totalCost/100000).toFixed(2)))} Lacs</span>
                                                                                <span style={{ color:'#3B6D11', fontWeight:600 }}>Billed: ₹{formatCurrencyIndian(Number((sStats.completedCost/100000).toFixed(2)))} Lacs</span>
                                                                                <span style={{ color:'#A32D2D', fontWeight:600 }}>Remaining: ₹{formatCurrencyIndian(Number(((sStats.totalCost-sStats.completedCost)/100000).toFixed(2)))} Lacs</span>
                                                                            </div>

                                                                            <AnimatePresence>
                                                                                {isSOpen && section.workstat && (
                                                                                    <motion.div initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:'auto' }}
                                                                                        exit={{ opacity:0, height:0 }} transition={{ duration:0.25 }}
                                                                                        style={{ borderTop:'1px solid #f1f5f9', background:'#fafbfc' }}>
                                                                                        {section.workstat.map((workstage) => {
                                                                                            const wcCost=calculateWorkstageCompletedCost(workstage);
                                                                                            const wtCost=parseFloat(workstage.cost||0);
                                                                                            const wPct=wtCost>0?Math.round((wcCost/wtCost)*100):0;
                                                                                            const wKey=`${section.id}-${workstage.id}`;
                                                                                            const isWOpen=openedWorkstages.includes(wKey);
                                                                                            const wColor=HEALTH[getHealth(wPct)].bar;

                                                                                            return (
                                                                                                <div key={workstage.id} style={{ borderBottom:'1px solid #f1f5f9' }}>
                                                                                                    <motion.div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', cursor:'pointer' }}
                                                                                                        onClick={()=>toggleWorkstage(section.id,workstage.id)}
                                                                                                        whileHover={{ backgroundColor:'#f5f7fa' }}>
                                                                                                        <div style={{ flex:1, minWidth:0 }}>
                                                                                                            <div style={{ fontSize:'12px', fontWeight:600, color:'#334155', marginBottom:'4px' }}>{workstage.name||'Unnamed Stage'}</div>
                                                                                                            <div style={{ display:'flex', gap:8, flexWrap:'wrap', fontSize:'11px' }}>
                                                                                                                <span style={{ padding:'2px 6px', borderRadius:4, background:'#f1f5f9', color:'#64748b' }}>Weight: {workstage.percentage||0}%</span>
                                                                                                                <span style={{ color:'#185FA5', fontWeight:600 }}>Total: ₹{formatCurrencyIndian(Number((wtCost/100000).toFixed(2)))} Lacs</span>
                                                                                                                {wcCost>0&&<span style={{ color:'#3B6D11', fontWeight:600 }}>Billed: ₹{formatCurrencyIndian(Number((wcCost/100000).toFixed(2)))} Lacs</span>}
                                                                                                                {(wtCost-wcCost)>0&&<span style={{ color:'#A32D2D', fontWeight:600 }}>Remaining: ₹{formatCurrencyIndian(Number(((wtCost-wcCost)/100000).toFixed(2)))} Lacs</span>}
                                                                                                            </div>
                                                                                                        </div>
                                                                                                        <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
                                                                                                            <div style={{ width:80 }}>
                                                                                                                <AnimBar pct={wPct} color={wColor} height={5} radius={3}/>
                                                                                                                <div style={{ fontSize:'10px', color:wColor, fontWeight:700, marginTop:2, textAlign:'right' }}>{wPct}%</div>
                                                                                                            </div>
                                                                                                            <motion.div style={{ color:'#94a3b8' }}
                                                                                                                animate={{ rotate:isWOpen?90:0 }} transition={{ type:'spring', stiffness:300, damping:25 }}>
                                                                                                                <ChevronRight size={14}/>
                                                                                                            </motion.div>
                                                                                                        </div>
                                                                                                    </motion.div>

                                                                                                    <AnimatePresence>
                                                                                                        {isWOpen && (
                                                                                                            <motion.div initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:'auto' }}
                                                                                                                exit={{ opacity:0, height:0 }} style={{ padding:'0 16px 12px 30px', background:'#fff' }}>

                                                                                                                {workstage.workstage_inputtype==='Input' && workstage.normal_input && (
                                                                                                                    <div>
                                                                                                                        <div style={{ fontSize:'11px', fontWeight:600, color:'#64748b', marginBottom:8, marginTop:6 }}>Input Items</div>
                                                                                                                        <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                                                                                                                            {workstage.normal_input.map((input,idx)=>{
                                                                                                                                let completedAmount=0, stageRows=[];
                                                                                                                                if (input.input_type==='Stage type') { completedAmount=input.stage?.reduce((s,x)=>s+parseFloat(x.completed_work||0),0)||0; stageRows=input.stage?.map(s=>({name:s.stage_name,pct:s.completed_percentage}))||[]; }
                                                                                                                                else if (input.input_type==='Normal Input') completedAmount=parseFloat(input.about_to_bill||0);
                                                                                                                                else if (input.input_type==='document'&&input.status==='completed') completedAmount=parseFloat(input.cost||0);
                                                                                                                                const inputTotal=parseFloat(input.cost||0);
                                                                                                                                const sc=input.status==='completed'?'#3B6D11':input.status==='in_progress'?'#185FA5':'#94a3b8';
                                                                                                                                return (
                                                                                                                                    <motion.div key={idx} initial={{ opacity:0, scale:0.95 }} animate={{ opacity:1, scale:1 }} transition={{ delay:idx*0.05 }}
                                                                                                                                        style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, padding:'10px 12px', minWidth:'220px', flex:1 }}>
                                                                                                                                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                                                                                                                                            <span style={{ fontSize:'11px', fontWeight:600, color:'#334155' }}>{input.input_type}</span>
                                                                                                                                            <span style={{ fontSize:'10px', padding:'2px 7px', borderRadius:10, background:sc+'20', color:sc, fontWeight:600 }}>{input.status||'pending'}</span>
                                                                                                                                        </div>
                                                                                                                                        {stageRows.map((d,i)=>(
                                                                                                                                            <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:'10px', color:'#64748b', padding:'2px 0', borderBottom:'1px solid #f1f5f9' }}>
                                                                                                                                                <span>{d.name}</span><span style={{ color:'#3B6D11', fontWeight:600 }}>{d.pct}%</span>
                                                                                                                                            </div>
                                                                                                                                        ))}
                                                                                                                                        <div style={{ marginTop:8, display:'flex', flexDirection:'column', gap:2, fontSize:'11px' }}>
                                                                                                                                            <span style={{ color:'#185FA5', fontWeight:600 }}>Total: ₹{formatCurrencyIndian(inputTotal)}</span>
                                                                                                                                            <span style={{ color:'#3B6D11', fontWeight:600 }}>Billed: ₹{formatCurrencyIndian(completedAmount)}</span>
                                                                                                                                            <span style={{ color:'#A32D2D', fontWeight:600 }}>Remaining: ₹{formatCurrencyIndian(inputTotal-completedAmount)}</span>
                                                                                                                                        </div>
                                                                                                                                    </motion.div>
                                                                                                                                );
                                                                                                                            })}
                                                                                                                        </div>
                                                                                                                    </div>
                                                                                                                )}

                                                                                                                {workstage.workstage_inputtype==='Stage' && workstage.stage && (
                                                                                                                    <div>
                                                                                                                        <div style={{ fontSize:'11px', fontWeight:600, color:'#64748b', marginBottom:8, marginTop:6 }}>Stage Items</div>
                                                                                                                        <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                                                                                                                            {workstage.stage.map((stage,idx)=>{
                                                                                                                                const sc=stage.status==='completed'?'#3B6D11':stage.status==='in_progress'?'#185FA5':'#94a3b8';
                                                                                                                                return (
                                                                                                                                    <motion.div key={idx} initial={{ opacity:0, scale:0.95 }} animate={{ opacity:1, scale:1 }} transition={{ delay:idx*0.05 }}
                                                                                                                                        style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, padding:'10px 12px', minWidth:'200px', flex:1 }}>
                                                                                                                                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                                                                                                                                            <span style={{ fontSize:'12px', fontWeight:600, color:'#334155' }}>{stage.stage_name}</span>
                                                                                                                                            <span style={{ fontSize:'10px', padding:'2px 7px', borderRadius:10, background:sc+'20', color:sc, fontWeight:600 }}>{stage.status}</span>
                                                                                                                                        </div>
                                                                                                                                        <AnimBar pct={stage.completed_percentage||0} color={sc} height={4} radius={2}/>
                                                                                                                                        <div style={{ display:'flex', justifyContent:'space-between', fontSize:'10px', marginTop:4 }}>
                                                                                                                                            <span style={{ color:'#3B6D11', fontWeight:600 }}>Billed: ₹{formatCurrencyIndian(parseFloat(stage.completed_work||0))}</span>
                                                                                                                                            <span style={{ color:'#185FA5', fontWeight:600 }}>Total: ₹{formatCurrencyIndian(parseFloat(stage.stage_amount||0))}</span>
                                                                                                                                        </div>
                                                                                                                                    </motion.div>
                                                                                                                                );
                                                                                                                            })}
                                                                                                                        </div>
                                                                                                                    </div>
                                                                                                                )}

                                                                                                                {workstage.workstage_inputtype==='Submission' && workstage.submission && (
                                                                                                                    <div>
                                                                                                                        <div style={{ fontSize:'11px', fontWeight:600, color:'#64748b', marginBottom:8, marginTop:6 }}>Submission Items</div>
                                                                                                                        <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                                                                                                                            {workstage.submission.map((sub,idx)=>{
                                                                                                                                const sc=sub.status==='completed'?'#3B6D11':'#94a3b8';
                                                                                                                                return (
                                                                                                                                    <div key={idx} style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, padding:'10px 12px', minWidth:'180px' }}>
                                                                                                                                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                                                                                                                                            <span style={{ fontSize:'12px', fontWeight:600, color:'#334155' }}>{sub.name||'Submission'}</span>
                                                                                                                                            <span style={{ fontSize:'10px', padding:'2px 7px', borderRadius:10, background:sc+'20', color:sc, fontWeight:600 }}>{sub.status}</span>
                                                                                                                                        </div>
                                                                                                                                        {sub.about_to_bill&&<div style={{ fontSize:'11px', color:'#3B6D11', fontWeight:600 }}>Billed: ₹{formatCurrencyIndian(parseFloat(sub.about_to_bill||0))}</div>}
                                                                                                                                    </div>
                                                                                                                                );
                                                                                                                            })}
                                                                                                                        </div>
                                                                                                                    </div>
                                                                                                                )}
                                                                                                            </motion.div>
                                                                                                        )}
                                                                                                    </AnimatePresence>
                                                                                                </div>
                                                                                            );
                                                                                        })}
                                                                                    </motion.div>
                                                                                )}
                                                                            </AnimatePresence>
                                                                        </motion.div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>
                    )}
                </div>
            </div>
        </>
    );
};

export default ProjectPageForHOD;